import React, { useContext, useState } from 'react';
import './vehicle-search-form.scss';
import { FMButton, FMInput, FMModal } from '@own/fds-react';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import parse from 'html-react-parser';
import { useOSBStep, useVehicleStep } from '../../../../../hooks/owners-osb';
import { VehicleData } from '../../../../../models/osb-model/osb-vehicle-step';
import ServiceHandler from '../../../../../services/service-handler';
import { formatRegVinNumber } from '../../osb-v3-utils';
import { PC_DEFAULT_DEALER_CODE } from '../../../../../constants';
import { useAllOSBState } from '../../../../../hooks/owners-osb/use-all-osb-state';
import { VehicleInfo } from '../../../../../models/osb-model/osb-vehicle-lookup';
import { buildNavigationUrl } from '../../../owners-osb/osb-utils';
import { OsbPathServiceStep } from '../../osb-view-controller';
import { useHistory } from 'react-router-dom';
import VinInformation from './vin-information';
import VehicleSearchFormSkeleton from './vehicle-search-form-skeleton';
import ServerContext from '../../../../../contexts/serverContext';
import { OSB_DEFAULT_MODEL_NAME } from '../../osb-v3-constant';

const InfoIconL = './icons/info-l.svg';
interface ApiError {
    errorCode: string;
    errorMessage: string;
    statusCode: number;
}
interface VehicleSearchFormProps {
    onContinue?: () => void;
    onVehicleInfoSuccess?: (info: VehicleInfo, imageUrl: string) => void;
}

const VehicleSearchForm: React.FC<VehicleSearchFormProps> = ({
    onContinue,
    onVehicleInfoSuccess,
}) => {
    const vehicleStepContent = useOSBContentFragment(
        'common/osb-v3',
        'vehicle-step'
    );
    const isLoading = !vehicleStepContent;
    const history = useHistory();
    const { osbStep } = useOSBStep();
    const { osbVehicleStep, setOSBVehicleStepPayload } = useVehicleStep();
    const bookable = useAllOSBState();
    const [vinRegErrorMsg, setVinRegErrorMsg] = useState<string>();
    const [mileageError, setMileageError] = useState<string>();
    const [isVinRegModalVisible, setIsVinRegModalVisible] = useState(false);
    const [serviceError, setServiceError] = useState('');
    const [loading, setLoading] = useState<boolean>(false);
    const { currentLanguageRegionCode, current3LetterCountryCode } = useContext(
        ServerContext
    );

    function isApiError(error: unknown): error is ApiError {
        return (
            typeof error === 'object' &&
            error !== null &&
            'errorCode' in error &&
            'errorMessage' in error &&
            'statusCode' in error
        );
    }
    const isThisVIN = (vinRegNo: string) => {
        return vinRegNo.length >= 10;
    };

    const isPatternMatch = (inputString: string, pattern?: string) => {
        if (!inputString || !pattern) {
            return false;
        }
        const regex = new RegExp(pattern.trim());
        return regex.test(inputString);
    };

    const validateVinRegNo = (vinRegNo: string) => {
        if (vinRegNo === '') {
            setVinRegErrorMsg(
                vehicleStepContent?.vinregistrationValidationMessage?.toString()
            );
            return false;
        }
        const validationPattern =
            vehicleStepContent?.vinregistrationDataPattern?.toString() ||
            '^([a-zA-Z0-9]{17}|[a-zA-Z0-9][a-zA-Z0-9 -]{1,7}[a-zA-Z0-9])$';
        if (isThisVIN(vinRegNo)) {
            //VIN
            const isVinValid = isPatternMatch(vinRegNo, validationPattern);
            if (!isVinValid) {
                setVinRegErrorMsg(
                    vehicleStepContent?.invalidVinErrorMessage?.toString()
                );
                return false;
            } else {
                setVinRegErrorMsg('');
                return true;
            }
        } else {
            // REG NO
            const isRegNoValid = isPatternMatch(vinRegNo, validationPattern);
            if (!isRegNoValid) {
                setVinRegErrorMsg(
                    vehicleStepContent?.invalidRegistrationErrorMessage?.toString()
                );
                return false;
            } else {
                setVinRegErrorMsg('');
                return true;
            }
        }
    };

    const onVinRegChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        validateVinRegNo(e.target.value);
        setOSBVehicleStepPayload({ vinRegNo: e.target.value });
    };

    const isValidMileage = (mileage: string) => {
        const sanitisedMileage = mileage.replace(/\D/g, '');
        setOSBVehicleStepPayload({ vinMileage: sanitisedMileage });
        if (sanitisedMileage === '') {
            setMileageError(
                vehicleStepContent?.mileageValidationMessage?.toString()
            );
            return false;
        }
        return true;
    };

    const onMileageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const isMileageValid = isValidMileage(e.target.value);
        if (!isMileageValid) {
            setMileageError(
                vehicleStepContent?.mileageValidationMessage?.toString()
            );
        } else {
            setMileageError('');
        }
    };

    const onCloseVinRegInfo = () => {
        setIsVinRegModalVisible(false);
    };

    const renderHowToFindYourVin = () => {
        setIsVinRegModalVisible(true);
    };

    const fetchVehicleImage = async (vin: string) => {
        return await ServiceHandler.VehicleTypeService.request(
            vin,
            currentLanguageRegionCode,
            current3LetterCountryCode?.toUpperCase()
        )
            .then(
                vehicleDetails =>
                    vehicleDetails?.vehicleData?.vehicleImage ?? '#'
            )
            .catch(error => {
                console.error('Failed to fetch vehicle image:', error);
                return '#';
            });
    };

    const prepareVehicleInfo = (vehicleInfo: VehicleInfo, imageUrl: string) => {
        osbVehicleStep.vehicleDetails = {
            modelName: vehicleInfo?.modelName || '',
            engine: vehicleInfo?.engine || '',
            transmission: vehicleInfo?.transmission || '',
            fuelType: vehicleInfo?.fuelType || '',
            color: vehicleInfo?.color || '',
            registrationNumber: vehicleInfo?.registrationNumber || '',
            vin: vehicleInfo?.vin || '',
            buildDate: vehicleInfo?.buildDate || '',
            vehicleImageURL: imageUrl,
        };
    };

    const buildVehicleDataPayload = (): VehicleData => {
        const isRegNumber = osbVehicleStep.vinRegNo.length <= 10;
        return isRegNumber
            ? {
                  registrationNumber: formatRegVinNumber(
                      osbVehicleStep.vinRegNo,
                      osbVehicleStep.allowSpecialCharacters
                  ),
                  mileage: osbVehicleStep.vinMileage,
              }
            : {
                  vin: formatRegVinNumber(
                      osbVehicleStep.vinRegNo,
                      osbVehicleStep.allowSpecialCharacters
                  ),
                  mileage: osbVehicleStep.vinMileage,
              };
    };

    const navigateToLocationStep = () => {
        osbVehicleStep.vehicleDetails.modelName = OSB_DEFAULT_MODEL_NAME;
        history.push(
            buildNavigationUrl(OsbPathServiceStep(), osbStep.UrlQueryParams)
        );
    };

    const handleSuccessfulVehicleLookup = async (
        result: any
    ): Promise<void> => {
        const imageUrl = await fetchVehicleImage(result.value.vin);
        prepareVehicleInfo(result.value, imageUrl);

        if (osbStep.isAuthenticatedFlow) {
            onVehicleInfoSuccess?.(result.value, imageUrl || '#');
            onContinue?.();
        } else {
            navigateToLocationStep();
        }
    };

    const handleVehicleLookupError = (error: unknown): void => {
        if (isApiError(error)) {
            console.error(
                `API Error: ${error.errorCode} (Status: ${error.statusCode}) - ${error.errorMessage}`
            );

            const errorMessage = error.errorCode;
            switch (errorMessage) {
                case 'OSB_VIN_EXISTS':
                    setServiceError('ACTIVE_BOOKING');
                    break;
                case 'OSB_NO_DATAFOUND':
                case 'OSB_VEHICLE_DETAILS_NOT_FOUND':
                case 'VEHICLE_NOT_FOUND':
                    setServiceError(
                        osbVehicleStep.vinRegNo.length <= 10
                            ? 'REG_NOT_FOUND'
                            : 'VIN_NOT_FOUND'
                    );
                    break;
                default:
                    setServiceError('SERVICE_ERROR');
                    break;
            }
        } else {
            console.error('An unexpected error occurred:', error);
            setServiceError('SERVICE_ERROR');
        }
    };

    const getVehicleData = async () => {
        const isVinRegEmpty = !osbVehicleStep.vinRegNo?.trim();
        const isMileageEmpty = !osbVehicleStep.vinMileage?.trim();

        if (isVinRegEmpty) validateVinRegNo('');
        if (isMileageEmpty) {
            setMileageError(
                vehicleStepContent?.mileageValidationMessage?.toString()
            );
        }
        if (isVinRegEmpty || isMileageEmpty) {
            return;
        }

        setLoading(true);
        setServiceError('');
        try {
            const vehicleData = buildVehicleDataPayload();
            const result = await ServiceHandler.OsbVehicleLookUp.postVehicleInfo(
                {
                    vehicleData,
                    prognosticsData: osbVehicleStep.vehiclePrognosticData,
                    dealerCode: PC_DEFAULT_DEALER_CODE,
                    bookable,
                }
            );

            await handleSuccessfulVehicleLookup(result);
        } catch (error) {
            handleVehicleLookupError(error);
        } finally {
            setLoading(false);
        }
    };

    function renderErrorMessages() {
        return (
            <>
                {serviceError && serviceError === 'ACTIVE_BOOKING' && (
                    <div className="error-message">
                        {vehicleStepContent?.activeBookingContent}
                    </div>
                )}
                {serviceError &&
                    ((serviceError === 'REG_NOT_FOUND' && (
                        <div className="error-message">
                            {vehicleStepContent?.regVehicleNotFoundMessage}
                        </div>
                    )) ||
                        (serviceError === 'VIN_NOT_FOUND' && (
                            <div className="error-message">
                                {vehicleStepContent?.vinVehicleNotFoundMessage}
                            </div>
                        )))}
                {serviceError && serviceError === 'SERVICE_ERROR' && (
                    <div className="error-message">
                        {vehicleStepContent?.vehicleServiceErrorMessage}
                    </div>
                )}
                {serviceError && serviceError === 'INTERNAL_ERROR_MESSAGE' && (
                    <div className="error-message">
                        {vehicleStepContent?.internalErrorMessage}
                    </div>
                )}
            </>
        );
    }
    if (isLoading) {
        return <VehicleSearchFormSkeleton />;
    }
    return (
        <>
            <div
                className="osb-v3-vehicle-search-container"
                data-testId="vehicle-search-section"
            >
                {serviceError !== '' && renderErrorMessages()}
                <div
                    className="vehicle-search-section-title"
                    data-testid="vehicle-search-title"
                >
                    {vehicleStepContent?.vehicleSectionTitle}
                </div>
                <div
                    className="vehicle-search-section-desc"
                    data-testid="vehicle-search-deac"
                >
                    {parse(
                        (
                            vehicleStepContent?.vehicleSectionDescLabel || ''
                        ).toString()
                    )}
                </div>

                <div className="vehicle-search-input-section">
                    <div className="vehicle-vin-reg-input-section">
                        <div className="vin-input-label">
                            <div className="osb-v3-reg-num-or-vin-label">
                                {vehicleStepContent?.vinregistrationLabel}
                            </div>
                            {/* eslint-disable-next-line */}
                            <div
                                data-testid="VinAndRegInfoPopup"
                                className="vin-reg-info-icon"
                                onClick={() => renderHowToFindYourVin()}
                                tabIndex={0}
                                role="button"
                            >
                                <img src={InfoIconL} alt="Info" />
                            </div>
                        </div>

                        <div className="osb-v3-reg-vin-input-wrapper">
                            <FMInput
                                dataTestId="vin-reg-input"
                                type="text"
                                onChange={e => onVinRegChange(e)}
                                value={osbVehicleStep.vinRegNo}
                                state={vinRegErrorMsg ? 'error' : 'standard'}
                                disabled={loading}
                            />
                        </div>
                        {vinRegErrorMsg ? (
                            <div className="osb-error-message">
                                {vinRegErrorMsg}
                            </div>
                        ) : (
                            <div className="vin-reg-input-placeholder">
                                {vehicleStepContent?.vinRegistrationPlaceholder}
                            </div>
                        )}
                    </div>
                    <div className="vehicle-mileage-input-section">
                        <div className="osb-v3-vehicle-mileage-label">
                            {vehicleStepContent?.mileageLabel}
                        </div>
                        <div className="osb-v3-vehicle-mileage-wrapper">
                            <FMInput
                                dataTestId="mileage-input"
                                type="text"
                                onChange={e => onMileageChange(e)}
                                value={osbVehicleStep.vinMileage}
                                state={mileageError ? 'error' : 'standard'}
                                disabled={loading}
                                ariaLabel="mileage-input"
                            />
                        </div>
                        {mileageError ? (
                            <div className="osb-error-message">
                                {mileageError}
                            </div>
                        ) : (
                            <div className="mileage-input-placeholder">
                                {vehicleStepContent?.mileagePlaceholder}
                            </div>
                        )}
                    </div>
                </div>
                <div className="cta-control-section">
                    <div className="continue-btn">
                        <FMButton
                            data-testid="continue-btn"
                            href=""
                            label={vehicleStepContent?.continueButtonLabel}
                            type="primary"
                            theme="light"
                            onClick={() => getVehicleData()}
                            disabled={loading}
                        />
                    </div>
                    <div className="do-not-know-details-btn">
                        <FMButton
                            href=""
                            label={vehicleStepContent?.iDontKnowDetailsLabel}
                            type="secondary"
                            theme="light"
                            disabled={loading}
                            onClick={() => navigateToLocationStep()}
                        />
                    </div>
                </div>
                {loading && osbStep.isAuthenticatedFlow && (
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                        {vehicleStepContent?.loadingIndicatorLabel}
                    </div>
                )}
            </div>

            <FMModal
                name="osb-v3-vin-reg-modal-popup"
                onClose={onCloseVinRegInfo}
                isVisible={isVinRegModalVisible}
            >
                <VinInformation />
            </FMModal>
        </>
    );
};

export default VehicleSearchForm;
